/**
 * Exécution budgétaire de l'exercice en cours et projection de clôture.
 *
 * Point de vigilance porté par la spécification § 4.1 : la comptabilité de
 * l'exercice en cours est tenue en encaissements et décaissements. Les
 * montants constatés sont des **flux de trésorerie**, jamais des charges
 * rattachées. Toute structure exposée ici porte cette distinction dans son
 * nommage (`flux…` contre `projete…`) afin que l'interface ne puisse pas les
 * confondre.
 */
import { atterrissageApplicable, COMPTES_HORS_NOMENCLATURE, conventionnelProjete, ecartResiduelArbitre, NOMENCLATURE, previsionnelApplicable, POSTES_CONVENTIONNELS, totalChargesVotees, totalConventionnelVote, totalProduitsVotes, versementsNotifies, poste as posteNomenclature, } from './configuration.js';
import { sommeExacte } from './adherents.js';
import { A_CLASSER, A_CLASSER_RECETTES, SOUS_POSTES_INITIAUX, familleSousPoste, } from './classement.js';
import { codeCompte, exerciceRattachement } from './ecritures.js';
export { codeCompte, exerciceRattachement } from './ecritures.js';
/**
 * Projection au 31/12 de la ligne cotisations (décision 41) : l'estimation de
 * fin de campagne quand elle porte sur l'exercice, sinon le montant voté (ou
 * reconduit). En janvier, tant que la campagne du nouvel exercice n'est pas
 * générée sur compagnies.org, l'estimation porte encore sur l'exercice
 * précédent : elle n'est pas appliquée.
 */
export function cotisationsProjetees(exercice, estimation) {
    if (estimation && estimation.campagne === exercice) {
        return { montant: estimation.montant, source: 'estimation', estimation };
    }
    return {
        montant: previsionnelApplicable(exercice).previsionnel.produits['cotisations'] ?? 0,
        source: 'vote',
        estimation: null,
    };
}
function estDansPeriode(e, debut, fin) {
    return e.date >= debut && e.date <= fin;
}
/**
 * Exécution constatée entre le 1er janvier et la date d'arrêté. Chaque
 * écriture est portée au sous-poste que lui donne `classement` ; celles qui
 * n'en ont pas forment une ligne « À classer ».
 */
export function fluxExercice(ecritures, exercice, dateArrete, classement, estimation) {
    const debut = `${exercice}-01-01`;
    const periode = ecritures.filter((e) => estDansPeriode(e, debut, dateArrete));
    const entrees = periode.filter((e) => e.sens === 'debit');
    const sorties = periode.filter((e) => e.sens === 'credit');
    const encaissements = sommeExacte(entrees.map((e) => e.montant));
    const decaissements = sommeExacte(sorties.map((e) => e.montant));
    const sortiesRattachees = sorties.filter((e) => exerciceRattachement(e).exercice === exercice);
    const decaissementsRattaches = sommeExacte(sortiesRattachees.map((e) => e.montant));
    const spPar = new Map(classement.sousPostes.map((s) => [s.id, s]));
    const sousPosteDe = (e) => {
        const id = classement.affectations.get(e.id);
        return id === undefined ? undefined : spPar.get(id);
    };
    // ── Produits ──────────────────────────────────────────────────────────
    // Un sous-poste de produit budgété s'agrège à son poste ; les autres
    // (hors budget) restent à la maille du sous-poste ; le reste est à classer.
    const ligneProduit = (e) => {
        const sp = sousPosteDe(e);
        if (!sp) {
            return { cle: A_CLASSER_RECETTES, libelle: libellePoste(A_CLASSER_RECETTES), parent: A_CLASSER };
        }
        const p = posteNomenclature(sp.poste);
        if (p?.nature === 'produit')
            return { cle: p.code, libelle: libellePoste(p.code), parent: p.code };
        return { cle: sp.id, libelle: sp.libelle, parent: sp.poste };
    };
    const parPoste = new Map();
    for (const e of entrees) {
        const { cle, libelle, parent } = ligneProduit(e);
        const cur = parPoste.get(cle) ?? { libelle, parent, montant: 0, n: 0 };
        cur.montant = sommeExacte([cur.montant, e.montant]);
        cur.n += 1;
        parPoste.set(cle, cur);
    }
    // Les postes budgétés sans aucun encaissement doivent apparaître à zéro :
    // trois ressources conventionnelles à zéro sont un message de gestion.
    const voteProduits = previsionnelApplicable(exercice).previsionnel.produits;
    for (const posteVote of Object.keys(voteProduits)) {
        if (!parPoste.has(posteVote)) {
            parPoste.set(posteVote, { libelle: libellePoste(posteVote), parent: posteVote, montant: 0, n: 0 });
        }
    }
    const cotisations = cotisationsProjetees(exercice, estimation);
    const plancher = (encaisse) => (atterrissageApplicable(dateArrete) ? encaisse : 0);
    const produitsParPoste = [...parPoste.entries()]
        .map(([cle, v]) => {
        const budgete = voteProduits[cle] ?? null;
        return {
            poste: cle,
            libelle: v.libelle,
            budgete,
            realise: v.montant,
            tauxRealisation: budgete !== null && budgete > 0 ? v.montant / budgete : null,
            // Les produits sont maintenus à leur cible budgétaire, sauf les
            // cotisations, projetées par `cotisationsProjetees`, et les ressources
            // conventionnelles notifiées (décision 42) ; les postes hors budget
            // sont projetés au montant déjà encaissé. Une cible ne descend jamais
            // sous l'encaissé (décision 45) : un versement de plus la relève.
            projete: cle === 'cotisations'
                ? cotisations.montant
                : POSTES_CONVENTIONNELS.includes(cle)
                    ? Math.max(conventionnelProjete(cle, exercice, dateArrete), plancher(v.montant))
                    : Math.max(budgete ?? v.montant, plancher(v.montant)),
            nbEcritures: v.n,
            posteParent: v.parent,
        };
    })
        .sort((a, b) => (b.budgete ?? 0) - (a.budgete ?? 0) || b.realise - a.realise || a.poste.localeCompare(b.poste));
    // ── Sorties rattachées : personnel et autres charges ──────────────────
    const ligneDe = (cle, libelle, parent, lignes) => {
        const realise = sommeExacte(lignes.map((e) => e.montant));
        return {
            poste: cle,
            libelle,
            budgete: null,
            realise,
            tauxRealisation: null,
            projete: realise,
            nbEcritures: lignes.length,
            posteParent: parent,
        };
    };
    const parSousPoste = new Map();
    const aClasser = [];
    for (const e of sortiesRattachees) {
        const sp = sousPosteDe(e);
        if (!sp) {
            aClasser.push(e);
            continue;
        }
        const l = parSousPoste.get(sp.id) ?? [];
        l.push(e);
        parSousPoste.set(sp.id, l);
    }
    const sousPostesTries = [...classement.sousPostes].sort((a, b) => a.ordre - b.ordre || a.id.localeCompare(b.id));
    // Une ligne de personnel ou de charge s'affiche même à zéro, sauf archivée ;
    // une ligne hors budget ou d'un sens inattendu seulement si elle a des écritures.
    // Un compte du plan comptable Zoho n'a de ligne que s'il a des écritures :
    // les dizaines de comptes jamais mouvementés n'encombrent pas l'exécution
    // (le générique de chaque poste, lui, reste affiché à zéro).
    const afficher = (s, attendue) => (parSousPoste.get(s.id)?.length ?? 0) > 0 || (attendue && !s.archive && s.origine !== 'zoho');
    const chargesPersonnel = sousPostesTries
        .filter((s) => familleSousPoste(s) === 'personnel' && afficher(s, true))
        .map((s) => ligneDe(s.id, s.libelle, s.poste, parSousPoste.get(s.id) ?? []));
    const totalChargesPersonnel = sommeExacte(chargesPersonnel.map((p) => p.realise));
    const autresCharges = sousPostesTries
        .filter((s) => familleSousPoste(s) !== 'personnel' && afficher(s, familleSousPoste(s) === 'charge'))
        .map((s) => ligneDe(s.id, s.libelle, s.poste, parSousPoste.get(s.id) ?? []));
    if (aClasser.length > 0) {
        autresCharges.push(ligneDe(A_CLASSER, libellePoste(A_CLASSER), A_CLASSER, aClasser));
    }
    const totalAutresCharges = sommeExacte(autresCharges.map((p) => p.realise));
    // ── Anomalies ─────────────────────────────────────────────────────────
    // Seules les sorties portent le champ personnalisé `cf_exercice_d_affectation` :
    // le signaler absent sur un encaissement serait un faux positif. Une ligne de
    // relevé non catégorisée (`uncategorized`) n'a pas encore ce champ : elle
    // n'est pas une anomalie de saisie, elle attend sa catégorisation.
    const sansExercice = sorties.filter((e) => e.exerciceAffectation === null && e.typeOperation !== 'uncategorized');
    const anomalies = [];
    if (sansExercice.length > 0) {
        anomalies.push({
            type: 'exercice_affectation_absent',
            libelle: "Décaissements sans champ « exercice d'affectation » : rattachement déduit de la date " +
                "d'opération.",
            occurrences: sansExercice.length,
            exemples: sansExercice.slice(0, 5).map((e) => `${e.date} — ${e.libelle || e.compteLibelle}`),
        });
    }
    return {
        exercice,
        dateArrete,
        encaissements,
        decaissements,
        decaissementsRattaches,
        decaissementsAutresExercices: sommeExacte([decaissements, -decaissementsRattaches]),
        soldeTresorerie: sommeExacte([encaissements, -decaissements]),
        produitsParPoste,
        chargesPersonnel,
        totalChargesPersonnel,
        autresCharges,
        totalAutresCharges,
        anomalies,
    };
}
/**
 * Libellé d'un poste de la nomenclature, d'un sous-poste initial (les
 * catégories hors budget y figurent) ou de la ligne « À classer ».
 */
export function libellePoste(code) {
    return (NOMENCLATURE.find((p) => p.code === code)?.libelle ??
        SOUS_POSTES_INITIAUX.find((s) => s.id === code)?.libelle ??
        (code === A_CLASSER || code === A_CLASSER_RECETTES ? 'À classer' : code));
}
/**
 * Agrège des lignes de sorties (personnel, autres charges) au poste de charge
 * de la nomenclature. Les sorties à classer pèsent provisoirement sur
 * `autres_postes` ; la ligne des recettes à classer, les lignes hors budget,
 * sans parent connu ou rattachées à un poste de produit sont écartées : elles ne doivent ni entrer dans
 * l'enveloppe des autres charges ni masquer le réalisé d'un produit.
 */
export function agregerParPoste(lignes) {
    const par = new Map();
    for (const l of lignes) {
        // Les recettes à classer partagent le parent `A_CLASSER` : jamais une charge.
        if (l.poste === A_CLASSER_RECETTES)
            continue;
        const parent = l.posteParent === A_CLASSER ? 'autres_postes' : (l.posteParent ?? l.poste);
        if (posteNomenclature(parent)?.nature !== 'charge')
            continue;
        const cur = par.get(parent) ?? { realise: 0, n: 0 };
        cur.realise = sommeExacte([cur.realise, l.realise]);
        cur.n += l.nbEcritures;
        par.set(parent, cur);
    }
    return [...par.entries()].map(([code, v]) => ({
        poste: code,
        libelle: libellePoste(code),
        budgete: null,
        realise: v.realise,
        tauxRealisation: null,
        projete: v.realise,
        nbEcritures: v.n,
        posteParent: code,
    }));
}
/**
 * Écritures composant une ligne d'exécution, avec les mêmes règles que
 * `fluxExercice()` : entrées de la période pour un produit, sorties rattachées
 * à l'exercice pour une charge.
 *
 * Une même clé peut nommer une ligne de produits et une ligne de charges (une
 * recette classée sur un sous-poste de charge) : `sens` restreint alors aux
 * entrées (`recette`) ou aux sorties rattachées (`depense`) de la ligne
 * cliquée. Sans `sens`, les deux sont rendues.
 */
export function ecrituresDuGroupe(ecritures, classement, cle, exercice, dateArrete, sens) {
    const debut = `${exercice}-01-01`;
    const spPar = new Map(classement.sousPostes.map((s) => [s.id, s]));
    return ecritures
        .filter((e) => estDansPeriode(e, debut, dateArrete))
        .filter((e) => sens === undefined || (sens === 'recette') === (e.sens === 'debit'))
        .filter((e) => {
        const id = classement.affectations.get(e.id);
        const sp = id === undefined ? undefined : spPar.get(id);
        if (e.sens === 'debit') {
            if (!sp)
                return cle === A_CLASSER_RECETTES;
            const p = posteNomenclature(sp.poste);
            return p?.nature === 'produit' ? p.code === cle : sp.id === cle;
        }
        if (exerciceRattachement(e).exercice !== exercice)
            return false;
        return sp ? sp.id === cle : cle === A_CLASSER;
    })
        .sort((a, b) => b.date.localeCompare(a.date));
}
/**
 * Projection de clôture confrontée au prévisionnel voté.
 * `depassement` provient de `depassementHorsNomenclature`, calculé sur les
 * écritures : le regroupement d'affichage ne recoupe pas la nomenclature.
 *
 * `chargesPostes` est l'atterrissage des postes budgétés, réalisé plus reste à
 * engager (décision 45) : les charges projetées en sont la somme, plus le
 * réalisé hors nomenclature, et l'écart d'arbitrage de séance ne s'applique
 * plus. Sans lui, la méthode historique : charges votées plus dépassements.
 */
export function projectionCloture(flux, depassement, estimation, chargesPostes) {
    const exercice = flux.exercice;
    const reference = previsionnelApplicable(exercice);
    const produitsVotes = totalProduitsVotes(exercice);
    const chargesVotees = totalChargesVotees(exercice);
    const cotisations = cotisationsProjetees(exercice, estimation);
    const cotisationsVotees = reference.previsionnel.produits['cotisations'] ?? 0;
    const encaisseCotisations = flux.produitsParPoste.find((p) => p.poste === 'cotisations')?.realise ?? 0;
    const encaisseConventionnel = sommeExacte(flux.produitsParPoste
        .filter((p) => POSTES_CONVENTIONNELS.includes(p.poste))
        .map((p) => p.realise));
    // L'encaissé de l'exercice comprend des paiements tardifs de la campagne
    // précédente : il peut dépasser l'estimation. Le reste à encaisser n'est
    // alors pas négatif, il est nul.
    const resteARecevoirCotisations = Math.max(0, sommeExacte([cotisations.montant, -encaisseCotisations]));
    const conventionnelVote = totalConventionnelVote(exercice);
    // Chaque ressource est projetée au plus élevé du notifié (ou voté) et de
    // l'encaissé : la projection d'exécution en porte le détail.
    const conventionnel = sommeExacte(flux.produitsParPoste.filter((p) => POSTES_CONVENTIONNELS.includes(p.poste)).map((p) => p.projete));
    const ecartConventionnel = sommeExacte([conventionnel, -conventionnelVote]);
    const resteARecevoirConventionnel = sommeExacte([conventionnel, -encaisseConventionnel]);
    const produitsProjetes = sommeExacte([
        produitsVotes,
        -cotisationsVotees,
        cotisations.montant,
        ecartConventionnel,
    ]);
    const ecartResiduel = chargesPostes === undefined ? ecartResiduelArbitre(exercice) : 0;
    const chargesProjetees = chargesPostes === undefined
        ? sommeExacte([chargesVotees, depassement, ecartResiduel])
        : sommeExacte([chargesPostes, depassement]);
    const resultatVote = sommeExacte([produitsVotes, -chargesVotees]);
    const resultatProjete = sommeExacte([produitsProjetes, -chargesProjetees]);
    return {
        exercice,
        /** Vrai quand aucun prévisionnel n'a été voté pour cet exercice. */
        previsionnelReconduit: reference.reconduit,
        exerciceSourcePrevisionnel: reference.exerciceSource,
        produitsVotes,
        produitsProjetes,
        cotisationsProjetees: cotisations.montant,
        sourceCotisations: cotisations.source,
        estimationCotisations: cotisations.estimation,
        ecartCotisations: sommeExacte([cotisations.montant, -cotisationsVotees]),
        conventionnelProjete: conventionnel,
        ecartConventionnel,
        versementsNotifies: versementsNotifies(exercice, flux.dateArrete),
        chargesVotees,
        chargesProjetees,
        depassementHorsNomenclature: depassement,
        ecartResiduel,
        methode: chargesPostes === undefined ? 'vote' : 'atterrissage',
        resultatVote,
        resultatProjete,
        ecartResultat: sommeExacte([resultatProjete, -resultatVote]),
        resteARecevoir: sommeExacte([resteARecevoirCotisations, resteARecevoirConventionnel]),
        resteARecevoirConventionnel,
        resteARecevoirCotisations,
    };
}
/**
 * Dépassement constaté sur les comptes hors nomenclature budgétaire.
 * Calculé directement sur les écritures : le regroupement d'affichage ne
 * recoupe pas la nomenclature budgétaire.
 */
export function depassementHorsNomenclature(ecritures, exercice, dateArrete) {
    const debut = `${exercice}-01-01`;
    const lignes = ecritures.filter((e) => e.sens === 'credit' &&
        estDansPeriode(e, debut, dateArrete) &&
        exerciceRattachement(e).exercice === exercice &&
        (() => {
            const c = codeCompte(e);
            return c !== null && COMPTES_HORS_NOMENCLATURE.includes(c);
        })());
    return sommeExacte(lignes.map((e) => e.montant));
}
/** Chaînage des fonds propres depuis la clôture précédente. */
export function chainageFondsPropres(params) {
    const fondsPropresCloture = sommeExacte([
        params.fondsPropresAvantAffectation,
        params.resultatExercicePrecedent,
    ]);
    const reserveAttendue = sommeExacte([fondsPropresCloture, params.resultatProjete]);
    return {
        fondsPropresAvantAffectation: params.fondsPropresAvantAffectation,
        resultatExercicePrecedent: params.resultatExercicePrecedent,
        fondsPropresCloture,
        ecartAuPrevisionnel: sommeExacte([fondsPropresCloture, -params.reservePrevueAuPrevisionnel]),
        resultatProjete: params.resultatProjete,
        reserveAttendue,
        seuilSecurite: params.seuilSecurite,
        multipleSeuil: params.seuilSecurite > 0 ? reserveAttendue / params.seuilSecurite : 0,
    };
}
//# sourceMappingURL=exercice.js.map